import Base from './Base';
import React, { useEffect, useState } from 'react';
import { Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { updateProfile, clearMessages, getUserDetails } from '../slices/commonReducer';

const ManageProfile = () => {
    const { name, email, role, mobile, errorMsg, successMsg } = useSelector((state) => state.common);
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        name: name || '',
        mobile: mobile || '',
        email: email || ''
    });

    const [isEditing, setIsEditing] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [id]: value,
        }));

        // Clear validation error for the field being edited
        if (validationErrors[id]) {
            setValidationErrors(prev => ({
                ...prev,
                [id]: ''
            }));
        }
    };

    // Validation function
    const validateForm = () => {
        const errors = {};

        // Name validation
        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Name must be at least 2 characters long';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
            errors.name = 'Name can only contain letters and spaces';
        }

        // Mobile validation
        if (!formData.mobile.trim()) {
            errors.mobile = 'Mobile number is required';
        } else if (!/^[0-9]{10}$/.test(formData.mobile.replace(/\s+/g, ''))) {
            errors.mobile = 'Mobile number must be exactly 10 digits';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        // Update form data when Redux state changes
        setFormData({
            name: name || '',
            mobile: mobile || '',
            email: email || ''
        });
    }, [name, mobile, email]);

    useEffect(() => {
        if (!!errorMsg) {
            toast.error(errorMsg, {
                position: 'top-center'
            });
        }
        if (!!successMsg) {
            toast.success(successMsg, {
                position: 'top-center'
            });
            // Disable editing mode after successful update
            setIsEditing(false);
        }
        dispatch(clearMessages());
        dispatch(getUserDetails());
    }, [successMsg, errorMsg, dispatch]);

    const handleEditClick = () => {
        setIsEditing(true);
        setValidationErrors({});
    };

    const handleCancelClick = () => {
        // Reset form data to original values
        setFormData({
            name: name || '',
            mobile: mobile || '',
            email: email || ''
        });
        setIsEditing(false);
        setValidationErrors({});
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const { name: formName, mobile: formMobile } = formData;
        console.log(formData);
        dispatch(updateProfile({ name: formName, mobile: formMobile }));
    };

    return (
        <Base>
            <Row>
                <Col xl={12} lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <div className="header-title">
                                <h4 className="fw-bold">Personal Information</h4>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="new-user-info">
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group controlId="name">
                                                <Form.Label className="!text-base">Name:</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    isInvalid={!!validationErrors.name}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.name}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="mobile">
                                                <Form.Label className="!text-base">Mobile Number:</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Mobile Number"
                                                    value={formData.mobile}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    isInvalid={!!validationErrors.mobile}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {validationErrors.mobile}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} className='mt-2'>
                                            <Form.Group controlId="email">
                                                <Form.Label className="!text-base">Email:</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    placeholder="Email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                                <Form.Text className="text-muted">
                                                    Email cannot be changed
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="mt-3">
                                        {!isEditing ? (
                                            <Button
                                                type="button"
                                                className="fw-bold"
                                                variant='primary'
                                                onClick={handleEditClick}
                                            >
                                                Edit Profile
                                            </Button>
                                        ) : (
                                            <div className="d-flex gap-2">
                                                <Button
                                                    type="submit"
                                                    variant='primary'
                                                    className='fw-bold'
                                                >
                                                    Save Changes
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant='outline-secondary'
                                                    className='no-hover fw-bold'
                                                    onClick={handleCancelClick}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Form>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Base>
    );
};

export default ManageProfile;